"use client";

import React, { useEffect, useRef, useState } from "react";
import styles from "../css/ScheduleConsult.module.css";
import { getPatients as fetchPatientsApi, getAppointments as fetchAppointmentsApi, createAppointment as createAppointmentApi } from "../../../../api/clinic/bookingApi.js";
import { useNavigate } from "react-router-dom";
import ErrorModal from "../../../../components/ErrorModal.jsx";
import Button from "../../../../components/Button.jsx";
import {
  formatISOToDDMMYYYY,
  getOperatingHoursForDateLocal,
  hhmmToMinutes,
  isClosedDateLocal,
  minutesToHHMM,
  toHMUTC,
  toISODateUTC,
} from "../utils/scheduleConsultUtils.js";

export default function ScheduleConsult() {
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [date, setDate] = useState("");
  const [dateISO, setDateISO] = useState("");
  const [time, setTime] = useState("");

  const [errors, setErrors] = useState({});
  const [patients, setPatients] = useState([]);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMsg, setErrorModalMsg] = useState("");
  const [availableTimes, setAvailableTimes] = useState([]);

  const wrapperRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const datePickerRef = useRef(null);

  const todayLocalISO = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  })();

  const checkSlotConflict = async (d, t) => {
    try {
      const appts = await fetchAppointmentsApi();
      const selDur = 30;
      const tStart = hhmmToMinutes(t);
      const tEnd = (tStart ?? 0) + selDur;
      return (Array.isArray(appts) ? appts : []).some((a) => {
        const ad = toISODateUTC(a.preferreddate);
        if (ad !== d) return false;
        const startHM = toHMUTC(a.preferredtime);
        const aStart = hhmmToMinutes(startHM);
        const aDur = 30;
        const aEnd = (aStart ?? 0) + aDur;
        return aStart != null && tStart != null && (tStart < aEnd && aStart < tEnd);
      });
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const computeAvailable = async () => {
      setAvailableTimes([]);
      if (!dateISO) return;

      const hours = getOperatingHoursForDateLocal(dateISO);
      if (!hours) {
        setAvailableTimes([]);
        setTime("");
        return;
      }
      try {
        const appts = await fetchAppointmentsApi();
        const selDur = 30;

        const now = new Date();
        const isToday = dateISO === todayLocalISO;
        const nowMin = now.getHours() * 60 + now.getMinutes();

        const operatingStart = hours.startMin;
        const operatingEnd = hours.endMin;
        const startMinRaw = isToday ? Math.max(nowMin, operatingStart) : operatingStart;
        const startMin = Math.max(operatingStart, Math.ceil(startMinRaw / selDur) * selDur);
        const lastStartMin = operatingEnd - selDur;

        const bookedIntervals = (Array.isArray(appts) ? appts : [])
          .filter((a) => toISODateUTC(a.preferreddate) === dateISO)
          .map((a) => {
            const startHM = toHMUTC(a.preferredtime);
            const start = hhmmToMinutes(startHM);
            const dur = 30;
            const end = Math.min(1440, (start ?? 0) + dur);
            return start == null ? null : [start, end];
          })
          .filter(Boolean);

        const overlaps = (s1, e1, s2, e2) => s1 < e2 && s2 < e1;

        const slots = [];
        if (startMin > lastStartMin) {
          setAvailableTimes([]);
          setTime("");
          return;
        }

        for (let m = startMin; m <= lastStartMin; m += selDur) {
          const s = m;
          const e = m + selDur;
          const conflict = bookedIntervals.some(([bs, be]) => overlaps(s, e, bs, be));
          if (!conflict) slots.push(minutesToHHMM(m));
        }

        setAvailableTimes(slots);
        if (!slots.includes(time)) {
          setTime(slots[0] || "");
        }
      } catch (e) {
        setAvailableTimes([]);
      }
    };
    computeAvailable();
  }, [dateISO]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (!inputValue.trim()) {
      setPatients([]);
      setShowPatientDropdown(false);
      return;
    }

    if (selectedPatient?.display === inputValue) {
      setPatients([]);
      setShowPatientDropdown(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchPatients(inputValue);
    }, 300);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [inputValue]);

  const searchPatients = async (query) => {
    try {
      const data = await fetchPatientsApi(query);
      console.log("🔍 Patients API Response:", data);

      const q = String(query || "").trim();
      const qLower = q.toLowerCase();

      const list = Array.isArray(data)
        ? data
            .filter((p) => {
              const idn = String(p.idnumber || "");
              const name = `${p.firstname || ""} ${p.surname || ""}`.trim();
              const nameLower = name.toLowerCase();

              return (
                q === "" ||
                idn.startsWith(q) ||
                nameLower.includes(qLower)
              );
            })
            .map((p) => ({
              id: p.patientid,
              idNumber: p.idnumber || "",
              name: `${p.firstname || ""} ${p.surname || ""}`.trim(),
              display: `${p.idnumber || ""} - ${(`${p.firstname || ""} ${p.surname || ""}`).trim()}`,
            }))
        : [];
      setPatients(list);
      setShowPatientDropdown(true);
    } catch (e) {
      setServerError(e.message || "Failed to search patients");
      setPatients([]);
      setShowPatientDropdown(false);
    }
  };

  const handlePatientSelect = (p) => {
    console.log("✅ Patient selected:", p);
    setSelectedPatient(p);
    setInputValue(p.display);
    setPatients([]);
    setShowPatientDropdown(false);
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.patientId;
      return copy;
    });
  };

  const handleInputChange = (val) => {
    if (selectedPatient && selectedPatient.display !== val) {
      setSelectedPatient(null);
    }
    setInputValue(val);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowPatientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validate = () => {
    const e = {};
    if (!selectedPatient?.id) e.patientId = "Required";
    if (!dateISO) e.date = "Required";
    else if (dateISO < todayLocalISO) e.date = "Date cannot be in the past";
    else if (isClosedDateLocal(dateISO)) e.date = "Clinic is closed on the selected date";
    if (!time) e.time = "Required";
    else if (!availableTimes.includes(time)) e.time = "Selected time is not available";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    console.log("🚀 Submitting with selectedPatient:", selectedPatient);

    setServerError("");
    setSuccessMsg("");

    const hasConflict = await checkSlotConflict(dateISO, time);
    if (hasConflict) {
      const msg = "Selected time is not available. Please choose another date and time.";
      setErrors((prev) => ({ ...prev, time: msg }));
      setErrorModalMsg(msg);
      setShowErrorModal(true);
      return;
    }

    setSubmitting(true);

    const payload = {
      patientid: selectedPatient.id,
      preferreddate: dateISO,
      preferredtime: time,
    };

    try {
      const res = await createAppointmentApi(payload);
      const createdAppointmentId =
        res?.data?.appointmentid ??
        res?.data?.appointmentId ??
        res?.data?.id ??
        null;
      const newAppt = {
        id: createdAppointmentId,
        patient: {
          id: selectedPatient.id,
          name: selectedPatient.name,
          idNumber: selectedPatient.idNumber || "",
        },
        service: "",
        serviceDetail: "",
        date: dateISO,
        time,
        status: "Pending",
      };
      try {
        const raw = localStorage.getItem("clinicAppointments");
        const list = raw ? JSON.parse(raw) : [];
        const updated = [newAppt, ...(Array.isArray(list) ? list : [])];
        localStorage.setItem("clinicAppointments", JSON.stringify(updated));
      } catch {}
      setSuccessMsg("Appointment booked successfully!");
      setSelectedPatient(null);
      setInputValue("");
      setDate("");
      setDateISO("");
      setTime("");
    } catch (e) {
      setServerError(e.message || "Error creating appointment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.field} ref={wrapperRef}>
        <label>Patient (Search by ID Number or Name)</label>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            if (patients.length > 0) setShowPatientDropdown(true);
          }}
          placeholder="Enter ID number or name to search..."
          autoComplete="off"
        />

        {showPatientDropdown && patients.length > 0 && (
          <ul className={styles.searchDropdown}>
            {patients.map((p) => (
              <li
                key={p.id}
                onMouseDown={() => handlePatientSelect(p)} // ✅ ensures selection happens before blur
                className={styles.searchDropdownItem}
              >
                {p.display}
              </li>
            ))}
          </ul>
        )}

        {!selectedPatient &&
          showPatientDropdown &&
          patients.length === 0 &&
          inputValue.trim() && (
            <div className={styles.noResults}>
              <div style={{ marginBottom: 8 }}>No patients found</div>
              <Button
                type="button"
                onMouseDown={() => navigate('/customer/profile/create', { state: { openAdd: true } })}
                variant="primary"
                ariaLabel="Create patient profile"
              >
                Create Profile
              </Button>
            </div>
          )}

        {errors.patientId && <span className={styles.error}>{errors.patientId}</span>}
      </div>

      <div className={styles.field}>
        <label>Preferred Date</label>
        <input
          type="text"
          value={date}
          readOnly
          onClick={() => {
            const el = datePickerRef.current;
            if (!el) return;
            if (typeof el.showPicker === "function") el.showPicker();
            else el.click();
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            const el = datePickerRef.current;
            if (!el) return;
            if (typeof el.showPicker === "function") el.showPicker();
            else el.click();
          }}
          placeholder="DD-MM-YYYY"
          inputMode="numeric"
          autoComplete="off"
        />
        <input
          ref={datePickerRef}
          type="date"
          value={dateISO}
          min={todayLocalISO}
          onChange={(e) => {
            const iso = e.target.value;
            setDateISO(iso);
            setDate(formatISOToDDMMYYYY(iso));
          }}
          style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }}
          tabIndex={-1}
          aria-hidden="true"
        />
        {errors.date && <span className={styles.error}>{errors.date}</span>}
      </div>

      <div className={styles.field}>
        <label>Preferred Time</label>
        {availableTimes.length > 0 ? (
          <div className={styles.compactTimePicker}>
            <div className={styles.timeSelector}>
              <select 
                className={styles.hourSelect}
                value={time.split(':')[0] || ''} 
                onChange={(e) => {
                  const newHour = e.target.value;
                  const currentMinute = time.split(':')[1] || '00';
                  const newTime = `${newHour}:${currentMinute}`;
                  // Find the closest available time with this hour
                  const availableWithHour = availableTimes.filter(t => t.split(':')[0] === newHour);
                  if (availableWithHour.length > 0) {
                    setTime(availableWithHour[0]);
                  }
                }}
              >
                {[...new Set(availableTimes.map(t => parseInt(t.split(':')[0])))].sort((a, b) => a - b).map(hour => (
                  <option key={hour} value={hour.toString().padStart(2, '0')}>
                    {hour.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
              <span className={styles.colon}>:</span>
              <select 
                className={styles.minuteSelect}
                value={time.split(':')[1] || '00'} 
                onChange={(e) => {
                  const newMinute = e.target.value;
                  const currentHour = time.split(':')[0] || '06';
                  const newTime = `${currentHour}:${newMinute}`;
                  // Only update if this exact time is available
                  if (availableTimes.includes(newTime)) {
                    setTime(newTime);
                  }
                }}
              >
                {[...new Set(availableTimes.filter(t => t.split(':')[0] === (time.split(':')[0] || '06')).map(t => t.split(':')[1]))].sort().map(minute => (
                  <option key={minute} value={minute}>
                    {minute}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className={styles.noTimes}>
            <span>No times available</span>
          </div>
        )}
        {errors.time && <span className={styles.error}>{errors.time}</span>}
      </div>

      {serverError && <div className={styles.errorBox}>{serverError}</div>}
      {successMsg && <div className={styles.successBox}>{successMsg}</div>}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={errorModalMsg}
      />

      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={submitting}>
          Book Appointment
        </Button>
      </div>
    </form>
  );
}
